import {
  Autocomplete,
  CircularProgress,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { getProjectCodeLibraryOptions } from 'api';
import { CodeLibraryOption, OptionFormat } from 'api/models/CodeLibrary';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import useQuery from 'hooks/useQuery';
import { query } from 'operations';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import { getLabel } from '../../utils';
import useLazyQuery from 'hooks/useLazyQuery';
import { Box } from '@mui/system';
import { FieldHelperText } from './FieldHelperText';

interface OptionAutocompleteProps {
  onChange: (value: string | string[]) => void;
  onBlur?: () => void;
  value: string | string[] | null;
  defaultValue: string | string[] | null;
  isMultiSelect: boolean;
  optionsSourceCodeLibraryId?: string;
  label: string;
  isRequired?: boolean;
  sx?: SxProps;
  error?: boolean;
  helperText?: string;
  description?: string;
  optionsSourceFieldTemplateId?: string;
  inputProps?: {
    readOnly?: boolean;
  };
  isImported?: boolean;
}

export default function OptionAutocompleteField({
  onChange,
  value,
  sx,
  isMultiSelect,
  optionsSourceCodeLibraryId,
  defaultValue,
  label,
  isRequired,
  error,
  helperText,
  description,
  inputProps,
  isImported,
}: OptionAutocompleteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { projectId } = useParams<GeneralIds>();
  const [autocompleteOptions, setAutocompleteOptions] = useState<
    CodeLibraryOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [codeOptionValues, setCodeOptionValues] = useState<
    CodeLibraryOption | CodeLibraryOption[] | null
  >(isMultiSelect ? [] : null);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isSelectableOptionsLoading, setIsSelectableOptionsLoading] =
    useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const libraryId = optionsSourceCodeLibraryId ?? 'medicine';

  const optionFormat = (autocompleteOptions ?? [])[0]?.codeLibrary?.config
    ?.optionFormat;

  useQuery<CodeLibraryOption[]>({
    type: ModelTypes.codeLibraryOption,
    ids: {
      id: libraryId,
      projectId,
    },
    queryParams: {
      pageSize: 50,
      pageNumber: 1,
      query: '',
      isValidNow: false,
    },
    set: {
      data: setAutocompleteOptions,
      loading: setIsLoading,
    },
  });

  const loadMoreResults = useLazyQuery<CodeLibraryOption[]>({
    type: ModelTypes.codeLibraryOption,
    ids: {
      id: optionsSourceCodeLibraryId,
      projectId,
    },
    queryParams: {
      pageSize: 50,
      pageNumber,
      query: searchQuery,
      isValidNow: false,
    },
    set: {
      data: newOptions => {
        setAutocompleteOptions(
          [...autocompleteOptions, ...newOptions].filter(
            (option, index, self) => {
              return index === self.findIndex(t => t.id === option.id);
            },
          ),
        );
        setPageNumber(pageNumber + 1);
      },
      loading: setIsSelectableOptionsLoading,
    },
  });

  async function onAutocompleteSearch(search: string) {
    const { data: options } = await query<CodeLibraryOption[]>({
      type: ModelTypes.codeLibraryOption,
      ids: {
        id: optionsSourceCodeLibraryId,
        projectId,
      },
      queryParams: {
        pageSize: 50,
        pageNumber: 1,
        query: search,
        isValidNow: false,
      },
    });
    setAutocompleteOptions(options);
    setIsLoading(false);
  }

  const debouncedSearch = useDebouncedCallback(onAutocompleteSearch, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsOptionsLoading(true);

      const val = value ?? defaultValue;

      if (
        ((Array.isArray(val) && val.length) || (!Array.isArray(val) && val)) &&
        optionsSourceCodeLibraryId
      ) {
        const libraries = await getProjectCodeLibraryOptions({
          projectId,
          codeLibraryId: optionsSourceCodeLibraryId,
          optionsIds: Array.isArray(val) ? val.filter(Boolean) : [val],
        });

        setCodeOptionValues(isMultiSelect ? libraries : libraries[0]);
      } else {
        setCodeOptionValues(
          Array.isArray(val) ? [] : isMultiSelect ? [] : null,
        );
      }
      setIsOptionsLoading(false);
    };

    fetchData();
  }, [
    optionsSourceCodeLibraryId,
    projectId,
    value,
    defaultValue,
    isMultiSelect,
  ]);

  if (codeOptionValues === null && isMultiSelect) {
    setCodeOptionValues([]);
  }

  return (
    <>
      <Autocomplete
        disabled={inputProps?.readOnly}
        multiple={isMultiSelect}
        fullWidth
        ChipProps={{
          color: 'primary',
        }}
        loading={isLoading}
        filterOptions={options => options}
        filterSelectedOptions
        loadingText={t('general.loading')}
        noOptionsText={t('general.noOptions')}
        onClose={() => {
          setAutocompleteOptions([]);
          setPageNumber(1);
        }}
        onOpen={() => {
          setIsLoading(true);
          onAutocompleteSearch(searchQuery);
        }}
        onChange={(_, newValue) => {
          if (isMultiSelect && Array.isArray(newValue)) {
            const ids = newValue?.map(option => option?.id ?? '');
            onChange(ids);
          } else if (!Array.isArray(newValue)) {
            onChange(newValue?.id ?? '');
          }
        }}
        options={autocompleteOptions}
        getOptionLabel={option => option?.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={codeOptionValues}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) ===
              listboxNode.scrollHeight
            ) {
              loadMoreResults();
            }
          },
        }}
        renderOption={(props, option) => {
          const isLastOption =
            autocompleteOptions.indexOf(option) ===
            autocompleteOptions.length - 1;
          return (
            <li {...props} key={`${option.id}`}>
              <Box
                display="flex"
                flexDirection={'column'}
                width="100%"
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Box display="flex" flexDirection={'row'} width="100%">
                  {optionFormat === OptionFormat.idAndTitle && (
                    <Typography variant="caption" color="text.secondary" mr={1}>
                      {option.externalId}
                    </Typography>
                  )}

                  <Typography>{option.name}</Typography>
                </Box>
                {isSelectableOptionsLoading && isLastOption && (
                  <CircularProgress color="inherit" size={20} />
                )}
              </Box>
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            sx={{
              '& .MuiInputBase-root': {
                background: isImported
                  ? theme => theme.palette.grey[200]
                  : null,
              },
              ...sx,
            }}
            label={getLabel({
              t,
              isRequired: isRequired ?? false,
              label,
            })}
            onChange={e => {
              setPageNumber(1);
              setSearchQuery(e.target.value);
              debouncedSearch(e.target.value);
              setIsLoading(true);
            }}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: isOptionsLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                params.InputProps.endAdornment
              ),
            }}
            error={error}
            helperText={<FieldHelperText
              description={description}
              helperText={helperText}
              error={error}
            />}
          />
        )}
      />
    </>
  );
}
